import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { PageHeader, StatCard, SectionCard, EmptyState } from "../components/ui/kaizen";
import { SchoolSchedulePanel } from "../components/amikobi/SchoolSchedulePanel";
import { AmikobiLeaderFollowups } from "../components/amikobi/AmikobiLeaderFollowups";
import { AmikobiRencontre } from "../components/amikobi/AmikobiRencontre";
import {
  GraduationCap, Users, Stack, ClipboardText, ChartLineUp, Plus, Trash,
  PencilSimple, X, CheckCircle, CalendarCheck, Notebook, IdentificationCard,
  PaperPlaneTilt, Warning, Bell, ClockCounterClockwise, FileText,
} from "@phosphor-icons/react";
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend } from "recharts";

const COHORT_COLORS = ["#D90429", "#f59e0b", "#10b981", "#3b82f6", "#a855f7", "#ec4899", "#14b8a6", "#eab308"];

const STATUT_CLS = {
  genere: "border-zinc-600 text-zinc-300",
  envoye: "border-sky-500/50 text-sky-300",
  accepte: "border-emerald-500/50 text-emerald-300",
  refuse: "border-rose-500/50 text-rose-300",
};

const TABS = [
  { key: "dashboard", label: "Tableau de bord" },
  { key: "pilote", label: "Projet pilote" },
  { key: "programs", label: "Programmes" },
  { key: "cohorts", label: "Cohortes" },
  { key: "horaire", label: "Horaire école" },
  { key: "students", label: "Élèves" },
  { key: "attendance", label: "Présences" },
  { key: "progress", label: "Progrès" },
  { key: "retention", label: "Rétention" },
  { key: "financement", label: "Financement & partenariats" },
  { key: "rencontre", label: "Présentation et rencontre" },
];

const ATT_STATUS = [
  { key: "present", label: "Présent", cls: "bg-emerald-500 text-zinc-950" },
  { key: "retard", label: "Retard", cls: "bg-amber-500 text-zinc-950" },
  { key: "absent", label: "Absent", cls: "bg-red-500 text-white" },
  { key: "excuse", label: "Excusé", cls: "bg-zinc-500 text-white" },
];

const PROG_CATS = ["technique", "discipline", "condition_physique", "attitude", "academique", "autre"];

const inputCls = "w-full rounded-lg bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm text-zinc-100 focus:border-amber-500 outline-none transition-colors";
const btnPrimary = "px-4 py-2.5 rounded-lg bg-amber-500 text-zinc-950 text-sm font-bold hover:bg-amber-400 active:scale-[0.98] transition-all flex items-center gap-2";
const btnGhost = "px-3 py-2 rounded-lg border border-zinc-800 text-zinc-300 text-sm hover:bg-zinc-800 transition-colors flex items-center gap-2";

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-lg rounded-xl bg-zinc-900 border border-zinc-800 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="amikobi-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-800">
          <h3 className="text-lg font-semibold text-zinc-50">{title}</h3>
          <button onClick={onClose} data-testid="modal-close-btn" className="text-zinc-500 hover:text-zinc-200"><X size={20} /></button>
        </div>
        <div className="p-6 max-h-[70vh] overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

export default function ProgrammeAmikobiModule() {
  const [tab, setTab] = useState("dashboard");
  const [overview, setOverview] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [students, setStudents] = useState([]);

  const loadCore = useCallback(async () => {
    try {
      const [ov, pr, co, st] = await Promise.all([
        api.get("/amikobi/overview"),
        api.get("/amikobi/programs"),
        api.get("/amikobi/cohorts"),
        api.get("/amikobi/students"),
      ]);
      setOverview(ov.data);
      setPrograms(pr.data.programs || []);
      setCohorts(co.data.cohorts || []);
      setStudents(st.data.students || []);
    } catch (e) { toast.error("Erreur de chargement Amikobi"); }
  }, []);

  useEffect(() => { loadCore(); }, [loadCore]);

  return (
    <div data-testid="programme-amikobi-module" className="max-w-7xl mx-auto">
      <PageHeader
        title="Programme Amikobi"
        subtitle="Programme éducatif et sportif Amikobi — présences & bulletins de progrès"
        icon={GraduationCap}
      />

      <div className="flex gap-1 mb-8 border-b border-zinc-800 overflow-x-auto" data-testid="amikobi-tabs">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`amikobi-tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === t.key ? "border-amber-500 text-amber-400" : "border-transparent text-zinc-400 hover:text-zinc-200"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "dashboard" && <DashboardTab overview={overview} />}
      {tab === "pilote" && <PilotTab reload={loadCore} onGoTab={setTab} />}
      {tab === "programs" && <ProgramsTab programs={programs} reload={loadCore} />}
      {tab === "cohorts" && <CohortsTab cohorts={cohorts} programs={programs} students={students} reload={loadCore} />}
      {tab === "horaire" && <SchoolSchedulePanel reload={loadCore} />}
      {tab === "students" && <StudentsTab students={students} programs={programs} reload={loadCore} />}
      {tab === "attendance" && <AttendanceTab cohorts={cohorts} />}
      {tab === "progress" && <ProgressTab students={students} programs={programs} />}
      {tab === "retention" && <RetentionTab />}
      {tab === "financement" && <FinancementTab />}
      {tab === "rencontre" && <AmikobiRencontre />}
    </div>
  );
}

/* ───────────────── Tableau de bord ───────────────── */
function DashboardTab({ overview }) {
  const [ret, setRet] = useState(null);
  useEffect(() => { api.get("/amikobi/retention-dashboard").then((r) => setRet(r.data)).catch(() => setRet(false)); }, []);
  if (!overview) return <div className="text-zinc-500 text-sm">Chargement…</div>;
  return (
    <div className="flex flex-col gap-8" data-testid="amikobi-dashboard">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        <StatCard label="Programmes" value={overview.programs} icon={GraduationCap} />
        <StatCard label="Cohortes" value={overview.cohorts} icon={Stack} />
        <StatCard label="Élèves" value={overview.students} icon={Users} />
        <StatCard label="Inscriptions actives" value={overview.enrollments} icon={IdentificationCard} />
        <StatCard label="Taux de présence" value={overview.attendance_rate != null ? `${overview.attendance_rate} %` : "—"} icon={CalendarCheck} tone="emerald" />
        <StatCard label="Présences enregistrées" value={overview.attendance_records} icon={ClipboardText} />
      </div>
      {ret && ret.series && <RetentionChart ret={ret} />}
    </div>
  );
}

function RetentionChart({ ret }) {
  // Fusionne les séries par date : { date, [cohortName]: rate, ... }
  const byDate = {};
  ret.series.forEach((s) => s.points.forEach((p) => {
    byDate[p.date] = byDate[p.date] || { date: p.date };
    byDate[p.date][s.cohort_name] = p.rate;
  }));
  const rows = Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5" data-testid="retention-dashboard">
      <SectionCard title="Taux de présence par cohorte" icon={ChartLineUp} className="lg:col-span-2">
        {rows.length === 0 ? (
          <EmptyState icon={ChartLineUp} title="Pas encore de données" description="Prends quelques présences pour voir la courbe de rétention apparaître." />
        ) : (
          <div style={{ width: "100%", height: 280 }}>
            <ResponsiveContainer>
              <LineChart data={rows} margin={{ top: 8, right: 12, bottom: 4, left: -18 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                <XAxis dataKey="date" tick={{ fill: "#a1a1aa", fontSize: 11 }} />
                <YAxis domain={[0, 100]} tick={{ fill: "#a1a1aa", fontSize: 11 }} unit="%" />
                <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #3f3f46", borderRadius: 8, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {ret.series.map((s, i) => (
                  <Line key={s.cohort_id} type="monotone" dataKey={s.cohort_name}
                    stroke={COHORT_COLORS[i % COHORT_COLORS.length]} strokeWidth={2} dot={{ r: 3 }} connectNulls />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </SectionCard>
      <SectionCard title={`Top élèves à risque (${ret.at_risk_count})`} icon={Warning}>
        {ret.top_at_risk.length === 0 ? (
          <div className="text-sm text-zinc-500">Aucun élève sous {ret.threshold}%. 🎉</div>
        ) : (
          <div className="flex flex-col gap-2">
            {ret.top_at_risk.map((a) => (
              <div key={a.student_id} data-testid={`dash-atrisk-${a.student_id}`} className="flex items-center justify-between gap-2 rounded-lg border border-zinc-800 bg-zinc-950/40 p-2.5">
                <span className="text-sm text-zinc-200 truncate">{a.full_name}</span>
                <span className={`px-2.5 py-1 rounded-md text-xs font-bold ${a.rate < 50 ? "bg-red-500 text-white" : "bg-amber-500 text-zinc-950"}`}>{a.rate}%</span>
              </div>
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
}

/* ───────────────── Programmes ───────────────── */
function ProgramsTab({ programs, reload }) {
  const [modal, setModal] = useState(null); // null | {} | program
  const empty = { name: "", description: "", school_year: "", discipline: "", active: true };
  const [form, setForm] = useState(empty);

  const open = (p) => { setForm(p || empty); setModal(p || {}); };
  const save = async () => {
    if (!form.name.trim()) return toast.error("Le nom est requis");
    try {
      if (form.id) await api.put(`/amikobi/programs/${form.id}`, form);
      else await api.post("/amikobi/programs", form);
      toast.success("Programme enregistré"); setModal(null); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const del = async (p) => {
    if (!window.confirm(`Supprimer « ${p.name} » ? (cohortes/inscriptions liées supprimées)`)) return;
    await api.delete(`/amikobi/programs/${p.id}`); toast.success("Supprimé"); reload();
  };

  return (
    <SectionCard title="Programmes" icon={GraduationCap}
      headerActions={<button className={btnPrimary} data-testid="add-program-btn" onClick={() => open(null)}><Plus size={16} /> Nouveau programme</button>}>
      {programs.length === 0 ? (
        <EmptyState icon={GraduationCap} title="Aucun programme" description="Crée ton premier programme éducatif et sportif (ex. Programme Amikobi 2026-2027)." actionLabel="Nouveau programme" onAction={() => open(null)} />
      ) : (
        <div className="flex flex-col gap-3">
          {programs.map((p) => (
            <div key={p.id} data-testid={`program-row-${p.id}`} className="flex items-center justify-between gap-4 rounded-lg border border-zinc-800 bg-zinc-950/40 p-4">
              <div className="min-w-0">
                <div className="text-zinc-100 font-semibold truncate">{p.name} {!p.active && <span className="text-xs text-zinc-500">(inactif)</span>}</div>
                <div className="text-xs text-zinc-400">{[p.school_year, p.discipline].filter(Boolean).join(" · ") || "—"}</div>
                {p.description && <div className="text-xs text-zinc-500 mt-1 truncate">{p.description}</div>}
              </div>
              <div className="flex gap-2 shrink-0">
                <button className={btnGhost} data-testid={`edit-program-${p.id}`} onClick={() => open(p)}><PencilSimple size={15} /></button>
                <button className={btnGhost} data-testid={`delete-program-${p.id}`} onClick={() => del(p)}><Trash size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      {modal && (
        <Modal title={form.id ? "Modifier le programme" : "Nouveau programme"} onClose={() => setModal(null)}>
          <div className="flex flex-col gap-4">
            <Field label="Nom du programme *"><input className={inputCls} data-testid="program-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Année scolaire"><input className={inputCls} value={form.school_year} onChange={(e) => setForm({ ...form, school_year: e.target.value })} placeholder="2026-2027" /></Field>
              <Field label="Discipline"><input className={inputCls} value={form.discipline} onChange={(e) => setForm({ ...form, discipline: e.target.value })} placeholder="BJJ, Muay Thaï…" /></Field>
            </div>
            <Field label="Description"><textarea className={inputCls} rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
            <label className="flex items-center gap-2 text-sm text-zinc-300"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Programme actif</label>
            <button className={btnPrimary + " justify-center"} data-testid="save-program-btn" onClick={save}><CheckCircle size={16} /> Enregistrer</button>
          </div>
        </Modal>
      )}
    </SectionCard>
  );
}

/* ───────────────── Cohortes ───────────────── */
function CohortsTab({ cohorts, programs, students, reload }) {
  const [modal, setModal] = useState(null);
  const [enrollFor, setEnrollFor] = useState(null); // cohort
  const empty = { program_id: programs[0]?.id || "", name: "", schedule: "", coach_name: "", active: true };
  const [form, setForm] = useState(empty);

  const open = (c) => { setForm(c || { ...empty, program_id: programs[0]?.id || "" }); setModal(c || {}); };
  const save = async () => {
    if (!form.name.trim() || !form.program_id) return toast.error("Nom et programme requis");
    try {
      if (form.id) await api.put(`/amikobi/cohorts/${form.id}`, form);
      else await api.post("/amikobi/cohorts", form);
      toast.success("Cohorte enregistrée"); setModal(null); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const del = async (c) => { if (!window.confirm(`Supprimer la cohorte « ${c.name} » ?`)) return; await api.delete(`/amikobi/cohorts/${c.id}`); toast.success("Supprimé"); reload(); };
  const sendBulletins = async (c) => {
    try {
      const dry = await api.post(`/amikobi/cohorts/${c.id}/email-bulletins?dry_run=true`);
      const n = dry.data.will_send;
      if (n === 0) return toast.error("Aucun parent avec courriel dans cette cohorte");
      if (!window.confirm(`Envoyer le bulletin à ${n} parent(s) de « ${c.name} » ?`)) return;
      const r = await api.post(`/amikobi/cohorts/${c.id}/email-bulletins?dry_run=false`);
      toast.success(`${r.data.sent} bulletin(s) envoyé(s)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur d'envoi"); }
  };
  const progName = (id) => programs.find((p) => p.id === id)?.name || "—";

  return (
    <SectionCard title="Cohortes / Groupes" icon={Stack}
      headerActions={<button className={btnPrimary} data-testid="add-cohort-btn" onClick={() => open(null)} disabled={programs.length === 0}><Plus size={16} /> Nouvelle cohorte</button>}>
      {programs.length === 0 ? (
        <EmptyState icon={Stack} title="Crée d'abord un programme" description="Les cohortes appartiennent à un programme." />
      ) : cohorts.length === 0 ? (
        <EmptyState icon={Stack} title="Aucune cohorte" description="Crée un groupe (ex. Groupe A — matin)." actionLabel="Nouvelle cohorte" onAction={() => open(null)} />
      ) : (
        <div className="flex flex-col gap-3">
          {cohorts.map((c) => (
            <div key={c.id} data-testid={`cohort-row-${c.id}`} className="flex items-center justify-between gap-4 rounded-lg border border-zinc-800 bg-zinc-950/40 p-4">
              <div className="min-w-0">
                <div className="text-zinc-100 font-semibold truncate">{c.name}</div>
                <div className="text-xs text-zinc-400">{progName(c.program_id)} · {c.schedule || "horaire n/d"} · {c.enrolled_count} inscrit(s)</div>
                {c.coach_name && <div className="text-xs text-zinc-500 mt-0.5">Coach : {c.coach_name}</div>}
              </div>
              <div className="flex gap-2 shrink-0">
                <button className={btnGhost} data-testid={`send-bulletins-${c.id}`} onClick={() => sendBulletins(c)}><PaperPlaneTilt size={15} /> Bulletins</button>
                <button className={btnGhost} data-testid={`enroll-cohort-${c.id}`} onClick={() => setEnrollFor(c)}><Users size={15} /> Inscrire</button>
                <button className={btnGhost} onClick={() => open(c)}><PencilSimple size={15} /></button>
                <button className={btnGhost} onClick={() => del(c)}><Trash size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      {modal && (
        <Modal title={form.id ? "Modifier la cohorte" : "Nouvelle cohorte"} onClose={() => setModal(null)}>
          <div className="flex flex-col gap-4">
            <Field label="Programme *"><select className={inputCls} data-testid="cohort-program-select" value={form.program_id} onChange={(e) => setForm({ ...form, program_id: e.target.value })}>{programs.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></Field>
            <Field label="Nom de la cohorte *"><input className={inputCls} data-testid="cohort-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Groupe A — matin" /></Field>
            <Field label="Horaire"><input className={inputCls} value={form.schedule} onChange={(e) => setForm({ ...form, schedule: e.target.value })} placeholder="Lun/Mer/Ven 8h-10h" /></Field>
            <Field label="Coach"><input className={inputCls} value={form.coach_name} onChange={(e) => setForm({ ...form, coach_name: e.target.value })} /></Field>
            <button className={btnPrimary + " justify-center"} data-testid="save-cohort-btn" onClick={save}><CheckCircle size={16} /> Enregistrer</button>
          </div>
        </Modal>
      )}
      {enrollFor && <EnrollModal cohort={enrollFor} students={students} onClose={() => { setEnrollFor(null); reload(); }} />}
    </SectionCard>
  );
}

function EnrollModal({ cohort, students, onClose }) {
  const [enrolled, setEnrolled] = useState([]);
  const load = useCallback(async () => {
    const r = await api.get(`/amikobi/enrollments?cohort_id=${cohort.id}`);
    setEnrolled(r.data.enrollments || []);
  }, [cohort.id]);
  useEffect(() => { load(); }, [load]);

  const enrolledIds = new Set(enrolled.map((e) => e.student_id));
  const add = async (sid) => {
    try { await api.post("/amikobi/enrollments", { student_id: sid, program_id: cohort.program_id, cohort_id: cohort.id }); toast.success("Inscrit"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const remove = async (eid) => { await api.delete(`/amikobi/enrollments/${eid}`); load(); };

  return (
    <Modal title={`Inscrire — ${cohort.name}`} onClose={onClose}>
      <div className="flex flex-col gap-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Inscrits ({enrolled.length})</div>
          {enrolled.length === 0 ? <div className="text-sm text-zinc-500">Aucun inscrit.</div> : (
            <div className="flex flex-col gap-2">
              {enrolled.map((e) => (
                <div key={e.id} className="flex items-center justify-between rounded-lg border border-zinc-800 px-3 py-2">
                  <span className="text-sm text-zinc-200">{e.student_name} {e.belt && <span className="text-xs text-zinc-500">· {e.belt}</span>}</span>
                  <button className="text-red-400 hover:text-red-300" data-testid={`unenroll-${e.id}`} onClick={() => remove(e.id)}><Trash size={15} /></button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Ajouter un élève</div>
          <div className="flex flex-col gap-2 max-h-56 overflow-y-auto">
            {students.filter((s) => !enrolledIds.has(s.id)).map((s) => (
              <button key={s.id} data-testid={`enroll-student-${s.id}`} onClick={() => add(s.id)} className="flex items-center justify-between rounded-lg border border-zinc-800 px-3 py-2 hover:border-amber-500/40 text-left">
                <span className="text-sm text-zinc-200">{s.full_name}</span>
                <Plus size={15} className="text-amber-400" />
              </button>
            ))}
            {students.length === 0 && <div className="text-sm text-zinc-500">Crée d'abord des élèves.</div>}
          </div>
        </div>
      </div>
    </Modal>
  );
}

/* ───────────────── Élèves ───────────────── */
function StudentsTab({ students, reload }) {
  const [modal, setModal] = useState(null);
  const [record, setRecord] = useState(null);
  const [importing, setImporting] = useState(false);
  const empty = { full_name: "", email: "", phone: "", birthdate: "", parent_name: "", parent_email: "", parent_phone: "", school: "", belt: "", notes: "" };
  const [form, setForm] = useState(empty);

  const open = (s) => { setForm(s || empty); setModal(s || {}); };
  const save = async () => {
    if (!form.full_name.trim()) return toast.error("Le nom est requis");
    try {
      if (form.id) await api.put(`/amikobi/students/${form.id}`, form);
      else await api.post("/amikobi/students", form);
      toast.success("Élève enregistré"); setModal(null); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const del = async (s) => { if (!window.confirm(`Supprimer l'élève ${s.full_name} ?`)) return; await api.delete(`/amikobi/students/${s.id}`); toast.success("Supprimé"); reload(); };

  return (
    <SectionCard title="Élèves" icon={Users}
      headerActions={<div className="flex gap-2">
        <button className={btnGhost} data-testid="import-students-btn" onClick={() => setImporting(true)}><Users size={16} /> Importer depuis Contrats</button>
        <button className={btnPrimary} data-testid="add-student-btn" onClick={() => open(null)}><Plus size={16} /> Nouvel élève</button>
      </div>}>
      {students.length === 0 ? (
        <EmptyState icon={Users} title="Aucun élève" description="Ajoute les élèves du programme, ou importe-les depuis tes contrats existants." actionLabel="Importer depuis Contrats" onAction={() => setImporting(true)} />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {students.map((s) => (
            <div key={s.id} data-testid={`student-row-${s.id}`} className="flex items-center justify-between gap-3 rounded-lg border border-zinc-800 bg-zinc-950/40 p-4">
              <button className="min-w-0 text-left" data-testid={`view-student-${s.id}`} onClick={() => setRecord(s)}>
                <div className="text-zinc-100 font-semibold truncate hover:text-amber-400 transition-colors">{s.full_name}</div>
                <div className="text-xs text-zinc-400 truncate">{[s.belt, s.school, s.email].filter(Boolean).join(" · ") || "—"}</div>
              </button>
              <div className="flex gap-2 shrink-0">
                <button className={btnGhost} data-testid={`edit-student-${s.id}`} onClick={() => open(s)}><PencilSimple size={15} /></button>
                <button className={btnGhost} data-testid={`delete-student-${s.id}`} onClick={() => del(s)}><Trash size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      {modal && (
        <Modal title={form.id ? "Modifier l'élève" : "Nouvel élève"} onClose={() => setModal(null)}>
          <div className="flex flex-col gap-4">
            <Field label="Nom complet *"><input className={inputCls} data-testid="student-name-input" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Courriel"><input className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
              <Field label="Téléphone"><input className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Date de naissance"><input type="date" className={inputCls} value={form.birthdate || ""} onChange={(e) => setForm({ ...form, birthdate: e.target.value })} /></Field>
              <Field label="Ceinture"><input className={inputCls} value={form.belt} onChange={(e) => setForm({ ...form, belt: e.target.value })} /></Field>
            </div>
            <Field label="École"><input className={inputCls} value={form.school} onChange={(e) => setForm({ ...form, school: e.target.value })} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Parent (nom)"><input className={inputCls} value={form.parent_name} onChange={(e) => setForm({ ...form, parent_name: e.target.value })} /></Field>
              <Field label="Parent (courriel)"><input className={inputCls} value={form.parent_email} onChange={(e) => setForm({ ...form, parent_email: e.target.value })} /></Field>
            </div>
            <Field label="Notes"><textarea className={inputCls} rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
            <button className={btnPrimary + " justify-center"} data-testid="save-student-btn" onClick={save}><CheckCircle size={16} /> Enregistrer</button>
          </div>
        </Modal>
      )}
      {record && <StudentRecordModal student={record} onClose={() => setRecord(null)} />}
      {importing && <ImportModal onClose={() => { setImporting(false); reload(); }} />}
    </SectionCard>
  );
}

function ImportModal({ onClose }) {
  const [candidates, setCandidates] = useState(null);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/amikobi/students/import-candidates").then((r) => setCandidates(r.data.candidates || [])).catch(() => setCandidates([])); }, []);
  const toggle = (id) => setSel((s) => ({ ...s, [id]: !s[id] }));
  const allIds = (candidates || []).map((c) => c.contract_id);
  const selectedIds = allIds.filter((id) => sel[id]);
  const toggleAll = () => { const all = selectedIds.length === allIds.length; const next = {}; if (!all) allIds.forEach((id) => (next[id] = true)); setSel(next); };
  const doImport = async () => {
    if (selectedIds.length === 0) return toast.error("Sélectionne au moins un élève");
    setBusy(true);
    try { const r = await api.post("/amikobi/students/import", { contract_ids: selectedIds }); toast.success(`${r.data.created} élève(s) importé(s)`); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };
  return (
    <Modal title="Importer depuis Contrats Élèves" onClose={onClose}>
      {candidates === null ? <div className="text-sm text-zinc-500">Chargement…</div> : candidates.length === 0 ? (
        <div className="text-sm text-zinc-500">Aucun contrat à importer (tous déjà importés).</div>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <button className={btnGhost} data-testid="import-select-all" onClick={toggleAll}>{selectedIds.length === allIds.length ? "Tout désélectionner" : "Tout sélectionner"}</button>
            <span className="text-xs text-zinc-500">{selectedIds.length}/{candidates.length} sélectionné(s)</span>
          </div>
          <div className="flex flex-col gap-1.5 max-h-72 overflow-y-auto">
            {candidates.map((c) => (
              <label key={c.contract_id} data-testid={`import-cand-${c.contract_id}`} className="flex items-center gap-3 rounded-lg border border-zinc-800 px-3 py-2 cursor-pointer hover:border-amber-500/40">
                <input type="checkbox" checked={!!sel[c.contract_id]} onChange={() => toggle(c.contract_id)} />
                <span className="text-sm text-zinc-200 flex-1 min-w-0 truncate">{c.full_name} <span className="text-xs text-zinc-500">{c.email || c.discipline}</span></span>
              </label>
            ))}
          </div>
          <button className={btnPrimary + " justify-center"} data-testid="do-import-btn" onClick={doImport} disabled={busy}><CheckCircle size={16} /> {busy ? "…" : `Importer ${selectedIds.length} élève(s)`}</button>
        </div>
      )}
    </Modal>
  );
}

function StudentRecordModal({ student, onClose }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/amikobi/students/${student.id}/record`).then((r) => setData(r.data)).catch(() => setData(false)); }, [student.id]);

  const downloadPdf = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/amikobi/students/${student.id}/bulletin.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url;
      a.download = `bulletin_${(student.full_name || "eleve").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Erreur PDF"); } finally { setBusy(false); }
  };
  const emailParent = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/amikobi/students/${student.id}/email-bulletin`, {});
      toast.success(`Bulletin envoyé à ${r.data.sent_to}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur d'envoi"); } finally { setBusy(false); }
  };

  return (
    <Modal title={`Bulletin — ${student.full_name}`} onClose={onClose}>
      {!data ? <div className="text-sm text-zinc-500">Chargement…</div> : (
        <div className="flex flex-col gap-5" data-testid="student-record">
          <div className="flex gap-2 flex-wrap">
            <button className={btnGhost} data-testid="download-bulletin-btn" onClick={downloadPdf} disabled={busy}><IdentificationCard size={15} /> Télécharger PDF</button>
            <button className={btnGhost} data-testid="email-bulletin-btn" onClick={emailParent} disabled={busy}><Notebook size={15} /> Envoyer au parent</button>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-lg border border-zinc-800 p-3 text-center"><div className="text-2xl font-bold text-amber-400">{data.attendance_rate != null ? `${data.attendance_rate}%` : "—"}</div><div className="text-xs text-zinc-500">Présence</div></div>
            <div className="rounded-lg border border-zinc-800 p-3 text-center"><div className="text-2xl font-bold text-zinc-100">{data.attendance_total}</div><div className="text-xs text-zinc-500">Cours suivis</div></div>
            <div className="rounded-lg border border-zinc-800 p-3 text-center"><div className="text-2xl font-bold text-zinc-100">{data.notes.length}</div><div className="text-xs text-zinc-500">Notes</div></div>
          </div>
          <FilesSection studentId={student.id} />
          <div>
            <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Notes de progrès</div>
            {data.notes.length === 0 ? <div className="text-sm text-zinc-500">Aucune note.</div> : (
              <div className="flex flex-col gap-2">
                {data.notes.map((n) => (
                  <div key={n.id} className="rounded-lg border border-zinc-800 p-3">
                    <div className="flex items-center justify-between"><span className="text-xs text-amber-400 uppercase tracking-wider">{n.category}</span><span className="text-xs text-zinc-500">{n.date}{n.rating ? ` · ${n.rating}/5` : ""}</span></div>
                    <div className="text-sm text-zinc-200 mt-1">{n.note}</div>
                    {n.coach_name && <div className="text-xs text-zinc-500 mt-1">— {n.coach_name}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

const FILE_CAT_LABELS = {
  certificat_medical: "Certificat médical", bulletin_scolaire: "Bulletin scolaire",
  formulaire: "Formulaire", photo: "Photo", autre: "Autre",
};

function FilesSection({ studentId }) {
  const [files, setFiles] = useState(null);
  const [category, setCategory] = useState("certificat_medical");
  const [busy, setBusy] = useState(false);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const load = useCallback(async () => {
    const r = await api.get(`/amikobi/students/${studentId}/files`);
    setFiles(r.data.files || []);
  }, [studentId]);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 25 * 1024 * 1024) { toast.error("Fichier trop volumineux (max 25 Mo)"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("category", category);
      await api.post(`/amikobi/students/${studentId}/files`, fd);
      toast.success("Fichier téléversé"); load();
    } catch (e2) { toast.error(e2?.response?.data?.detail || "Échec du téléversement"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  const del = async (id) => { if (!window.confirm("Supprimer ce fichier ?")) return; await api.delete(`/amikobi/files/${id}`); toast.success("Supprimé"); load(); };

  return (
    <div data-testid="student-files">
      <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Documents</div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="file-category-select"
          className="rounded-lg bg-zinc-950 border border-zinc-800 px-3 py-2 text-sm text-zinc-100 focus:border-amber-500 outline-none">
          {Object.entries(FILE_CAT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <label className={btnGhost + " cursor-pointer"} data-testid="file-upload-label">
          <IdentificationCard size={15} /> {busy ? "Téléversement…" : "Téléverser un fichier"}
          <input type="file" className="hidden" onChange={upload} disabled={busy}
            accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.doc,.docx" data-testid="file-upload-input" />
        </label>
      </div>
      {files === null ? <div className="text-sm text-zinc-500">Chargement…</div> : files.length === 0 ? (
        <div className="text-sm text-zinc-500 mb-2">Aucun document. Ajoute un certificat médical, un bulletin ou un formulaire.</div>
      ) : (
        <div className="flex flex-col gap-2 mb-2">
          {files.map((f) => (
            <div key={f.id} data-testid={`file-row-${f.id}`} className="flex items-center justify-between gap-3 rounded-lg border border-zinc-800 bg-zinc-950/40 p-2.5">
              <a href={`${BACKEND}${f.url}`} target="_blank" rel="noreferrer" className="min-w-0 flex-1 hover:text-amber-400 transition-colors">
                <div className="text-sm text-zinc-200 truncate">{f.original_filename}</div>
                <div className="text-xs text-zinc-500">{FILE_CAT_LABELS[f.category] || f.category} · {Math.round((f.size || 0) / 1024)} Ko</div>
              </a>
              <button className="text-red-400 hover:text-red-300 shrink-0" data-testid={`file-delete-${f.id}`} onClick={() => del(f.id)}><Trash size={15} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
function AttendanceTab({ cohorts }) {
  const [cohortId, setCohortId] = useState(cohorts[0]?.id || "");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [roster, setRoster] = useState([]);
  const [marks, setMarks] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!cohortId) { setRoster([]); return; }
    const [enr, att] = await Promise.all([
      api.get(`/amikobi/enrollments?cohort_id=${cohortId}`),
      api.get(`/amikobi/attendance?cohort_id=${cohortId}&date=${date}`),
    ]);
    setRoster(enr.data.enrollments || []);
    const m = {};
    (att.data.attendance || []).forEach((a) => { m[a.student_id] = a.status; });
    setMarks(m);
  }, [cohortId, date]);
  useEffect(() => { load(); }, [load]);

  const setMark = (sid, status) => setMarks((m) => ({ ...m, [sid]: status }));
  const save = async () => {
    setBusy(true);
    try {
      const records = roster.map((r) => ({ student_id: r.student_id, status: marks[r.student_id] || "present", note: "" }));
      await api.post("/amikobi/attendance/bulk", { cohort_id: cohortId, date, records });
      toast.success("Présences enregistrées");
    } catch (e) { toast.error("Erreur"); } finally { setBusy(false); }
  };
  const exportCsv = async () => {
    try {
      const url = cohortId ? `/amikobi/attendance/export.csv?cohort_id=${cohortId}` : "/amikobi/attendance/export.csv";
      const r = await api.get(url, { responseType: "blob" });
      const href = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a"); a.href = href; a.download = "amikobi_presences.csv";
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(href);
    } catch (e) { toast.error("Erreur export"); }
  };

  return (
    <SectionCard title="Feuille de présence" icon={CalendarCheck}>
      {cohorts.length === 0 ? (
        <EmptyState icon={CalendarCheck} title="Aucune cohorte" description="Crée une cohorte et inscris des élèves pour prendre les présences." />
      ) : (
        <>
          <div className="flex flex-wrap gap-3 mb-5">
            <select className={inputCls + " max-w-xs"} data-testid="attendance-cohort-select" value={cohortId} onChange={(e) => setCohortId(e.target.value)}>
              {cohorts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <input type="date" className={inputCls + " max-w-[180px]"} data-testid="attendance-date-input" value={date} onChange={(e) => setDate(e.target.value)} />
            <button className={btnPrimary} data-testid="save-attendance-btn" onClick={save} disabled={busy || roster.length === 0}><CheckCircle size={16} /> {busy ? "…" : "Enregistrer"}</button>
            <button className={btnGhost} data-testid="export-attendance-btn" onClick={exportCsv}><IdentificationCard size={16} /> Exporter CSV</button>
          </div>
          {roster.length === 0 ? <div className="text-sm text-zinc-500">Aucun élève inscrit dans cette cohorte.</div> : (
            <div className="flex flex-col gap-2">
              {roster.map((r) => (
                <div key={r.id} data-testid={`attendance-row-${r.student_id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-zinc-800 bg-zinc-950/40 p-3">
                  <span className="text-sm text-zinc-100">{r.student_name}</span>
                  <div className="flex gap-1.5">
                    {ATT_STATUS.map((s) => (
                      <button key={s.key} data-testid={`att-${r.student_id}-${s.key}`} onClick={() => setMark(r.student_id, s.key)}
                        className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${(marks[r.student_id] || "present") === s.key ? s.cls : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"}`}>
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </SectionCard>
  );
}

/* ───────────────── Progrès ───────────────── */
function ProgressTab({ students, programs }) {
  const [studentId, setStudentId] = useState(students[0]?.id || "");
  const [notes, setNotes] = useState([]);
  const [form, setForm] = useState({ category: "technique", note: "", rating: "", program_id: "" });

  const load = useCallback(async () => {
    if (!studentId) { setNotes([]); return; }
    const r = await api.get(`/amikobi/progress?student_id=${studentId}`);
    setNotes(r.data.notes || []);
  }, [studentId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.note.trim()) return toast.error("Écris une note");
    try {
      await api.post("/amikobi/progress", {
        student_id: studentId, program_id: form.program_id || "", category: form.category,
        note: form.note, rating: form.rating ? Number(form.rating) : null,
      });
      toast.success("Note ajoutée"); setForm({ ...form, note: "", rating: "" }); load();
    } catch (e) { toast.error("Erreur"); }
  };
  const del = async (id) => { await api.delete(`/amikobi/progress/${id}`); load(); };

  return (
    <SectionCard title="Notes de progrès" icon={Notebook}>
      {students.length === 0 ? (
        <EmptyState icon={Notebook} title="Aucun élève" description="Ajoute des élèves pour saisir des notes de progrès." />
      ) : (
        <>
          <select className={inputCls + " max-w-xs mb-5"} data-testid="progress-student-select" value={studentId} onChange={(e) => setStudentId(e.target.value)}>
            {students.map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}
          </select>
          <div className="rounded-lg border border-zinc-800 p-4 mb-5 flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Catégorie"><select className={inputCls} data-testid="progress-category-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>{PROG_CATS.map((c) => <option key={c} value={c}>{c.replace("_", " ")}</option>)}</select></Field>
              <Field label="Note /5 (optionnel)"><input type="number" min="1" max="5" className={inputCls} value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} /></Field>
            </div>
            <Field label="Observation"><textarea className={inputCls} rows={2} data-testid="progress-note-input" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Ex. Belle progression sur la garde fermée…" /></Field>
            <button className={btnPrimary + " self-start"} data-testid="add-progress-btn" onClick={add}><Plus size={16} /> Ajouter la note</button>
          </div>
          {notes.length === 0 ? <div className="text-sm text-zinc-500">Aucune note pour cet élève.</div> : (
            <div className="flex flex-col gap-2">
              {notes.map((n) => (
                <div key={n.id} className="rounded-lg border border-zinc-800 bg-zinc-950/40 p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-amber-400 uppercase tracking-wider">{n.category}</span>
                    <div className="flex items-center gap-3"><span className="text-xs text-zinc-500">{n.date}{n.rating ? ` · ${n.rating}/5` : ""}</span><button className="text-red-400 hover:text-red-300" onClick={() => del(n.id)}><Trash size={14} /></button></div>
                  </div>
                  <div className="text-sm text-zinc-200 mt-1">{n.note}</div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </SectionCard>
  );
}

/* ───────────────── Rétention (alertes de présence) ───────────────── */
function RetentionTab() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ attendance_alert_enabled: false, attendance_threshold: 70, min_sessions: 4 });

  const load = useCallback(async () => {
    const r = await api.get("/amikobi/attendance-alerts");
    setData(r.data);
    setForm(r.data.settings);
  }, []);
  useEffect(() => { load(); }, [load]);

  const [history, setHistory] = useState(null);
  const loadHistory = useCallback(async () => {
    const r = await api.get("/amikobi/send-history");
    setHistory(r.data.history || []);
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const saveSettings = async () => {
    setSaving(true);
    try { await api.put("/amikobi/settings", form); toast.success("Réglages enregistrés"); load(); }
    catch (e) { toast.error("Erreur"); } finally { setSaving(false); }
  };
  const sendReminders = async () => {
    try {
      const dry = await api.post("/amikobi/attendance-alerts/send?dry_run=true");
      if (dry.data.will_send === 0) return toast.error("Aucun parent à relancer (pas de courriel ou aucun élève à risque)");
      if (!window.confirm(`Envoyer un rappel à ${dry.data.will_send} parent(s) ?`)) return;
      setSending(true);
      const r = await api.post("/amikobi/attendance-alerts/send?dry_run=false");
      toast.success(`${r.data.sent} rappel(s) envoyé(s)${r.data.skipped_recent ? ` · ${r.data.skipped_recent} ignoré(s) (récents)` : ""}`);
      load(); loadHistory();
    } catch (e) { toast.error("Erreur d'envoi"); } finally { setSending(false); }
  };
  const sendDigest = async () => {
    try {
      const dry = await api.post("/amikobi/weekly-digest/send?dry_run=true");
      if (!dry.data.to) return toast.error("Aucun courriel admin configuré sur ton compte");
      if (!window.confirm(`M'envoyer le résumé hebdo à ${dry.data.to} ?`)) return;
      await api.post("/amikobi/weekly-digest/send?dry_run=false");
      toast.success(`Résumé envoyé à ${dry.data.to}`); loadHistory();
    } catch (e) { toast.error("Erreur d'envoi"); }
  };

  return (
    <div className="flex flex-col gap-6" data-testid="amikobi-retention">
      <SectionCard title="Alertes de présence" icon={Bell}>
        <p className="text-sm text-zinc-400 mb-5">
          Repère les élèves dont l'assiduité baisse et relance automatiquement leurs parents chaque semaine.
          Un même parent n'est pas relancé plus d'une fois par 7 jours.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
          <Field label="Seuil de présence (%)">
            <input type="number" min="0" max="100" className={inputCls} data-testid="threshold-input"
              value={form.attendance_threshold} onChange={(e) => setForm({ ...form, attendance_threshold: Number(e.target.value) })} />
          </Field>
          <Field label="Cours suivis min. avant alerte">
            <input type="number" min="1" className={inputCls} data-testid="minsessions-input"
              value={form.min_sessions} onChange={(e) => setForm({ ...form, min_sessions: Number(e.target.value) })} />
          </Field>
          <label className="flex items-center gap-2 text-sm text-zinc-300 mt-6" data-testid="alert-enabled-toggle">
            <input type="checkbox" checked={form.attendance_alert_enabled}
              onChange={(e) => setForm({ ...form, attendance_alert_enabled: e.target.checked })} />
            Rappels automatiques hebdo
          </label>
          <label className="flex items-center gap-2 text-sm text-zinc-300 mt-6" data-testid="digest-enabled-toggle">
            <input type="checkbox" checked={form.weekly_digest_enabled || false}
              onChange={(e) => setForm({ ...form, weekly_digest_enabled: e.target.checked })} />
            Résumé hebdo par courriel (à moi)
          </label>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button className={btnPrimary} data-testid="save-settings-btn" onClick={saveSettings} disabled={saving}><CheckCircle size={16} /> {saving ? "…" : "Enregistrer"}</button>
          <button className={btnGhost} data-testid="send-reminders-btn" onClick={sendReminders} disabled={sending}><PaperPlaneTilt size={16} /> {sending ? "…" : "Envoyer les rappels maintenant"}</button>
          <button className={btnGhost} data-testid="send-digest-btn" onClick={sendDigest}><Bell size={16} /> M'envoyer le résumé maintenant</button>
        </div>
      </SectionCard>

      <SectionCard title={`Élèves à risque${data ? ` (${data.count})` : ""}`} icon={Warning}>
        {!data ? <div className="text-sm text-zinc-500">Chargement…</div> : data.at_risk.length === 0 ? (
          <EmptyState icon={CheckCircle} title="Aucun élève à risque" description={`Aucun élève sous ${form.attendance_threshold}% (min. ${form.min_sessions} cours).`} />
        ) : (
          <div className="flex flex-col gap-2">
            {data.at_risk.map((a) => (
              <div key={a.student_id} data-testid={`atrisk-${a.student_id}`} className="flex items-center justify-between gap-3 rounded-lg border border-zinc-800 bg-zinc-950/40 p-3">
                <div className="min-w-0">
                  <div className="text-sm text-zinc-100 truncate">{a.full_name} {a.belt && <span className="text-xs text-zinc-500">· {a.belt}</span>}</div>
                  <div className="text-xs text-zinc-500">{a.email || "aucun courriel"} · {a.total} cours</div>
                </div>
                <span className={`px-3 py-1 rounded-md text-sm font-bold ${a.rate < 50 ? "bg-red-500 text-white" : "bg-amber-500 text-zinc-950"}`}>{a.rate}%</span>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      <SectionCard title="Historique des envois" icon={ClockCounterClockwise}>
        {!history ? <div className="text-sm text-zinc-500">Chargement…</div> : history.length === 0 ? (
          <div className="text-sm text-zinc-500">Aucun courriel Amikobi envoyé pour l'instant.</div>
        ) : (
          <div className="flex flex-col gap-2 max-h-96 overflow-y-auto" data-testid="amikobi-send-history">
            {history.map((h) => (
              <div key={h.id} className="flex items-center justify-between gap-3 rounded-lg border border-zinc-800 bg-zinc-950/40 p-3">
                <div className="min-w-0">
                  <div className="text-sm text-zinc-200 truncate">{(h.to || []).join(", ")}</div>
                  <div className="text-xs text-zinc-500 truncate">{HISTORY_LABELS[h.module] || h.module} · {(h.created_at || "").slice(0, 16).replace("T", " ")}</div>
                </div>
                <span className={`px-2.5 py-1 rounded-md text-xs font-semibold shrink-0 ${h.status === "sent" ? "bg-emerald-500/20 text-emerald-400" : "bg-red-500/20 text-red-400"}`}>
                  {h.status === "sent" ? "Envoyé" : "Échec"}
                </span>
              </div>
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
}

const HISTORY_LABELS = {
  amikobi_bulletin: "Bulletin (individuel)",
  amikobi_bulletin_bulk: "Bulletin (groupé)",
  amikobi_attendance_alert: "Rappel de présence",
};

function Field({ label, children }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs uppercase tracking-wider text-zinc-500 font-medium">{label}</span>
      {children}
    </label>
  );
}

/* ───────────────── Financement & partenariats ───────────────── */
function FinancementTab() {
  const [doc, setDoc] = useState(null);
  const [ai, setAi] = useState(null); // { type, loading, data }
  const [mailTo, setMailTo] = useState("");
  const [mailSubject, setMailSubject] = useState("Programme Amikobi — Kickboxing / Muay thaï");
  const [mailBody, setMailBody] = useState("");
  const [mailBusy, setMailBusy] = useState(false);
  const [devisP, setDevisP] = useState({});
  const [devisEcole, setDevisEcole] = useState("");
  const [attachDevis, setAttachDevis] = useState(false);
  const [attachIdx, setAttachIdx] = useState(0);
  const [attachP, setAttachP] = useState(12);
  const [devisHistory, setDevisHistory] = useState([]);

  const loadDevisHistory = async () => {
    try { const { data } = await api.get("/amikobi/program/devis-history"); setDevisHistory(data.history || []); }
    catch { /* noop */ }
  };
  useEffect(() => { loadDevisHistory(); }, []);

  const setDevisStatut = async (id, statut) => {
    try {
      await api.post(`/amikobi/program/devis/${id}/status`, { statut });
      setDevisHistory((prev) => prev.map((h) => (h.id === id ? { ...h, statut } : h)));
      toast.success("Statut du devis mis à jour");
    } catch { toast.error("Échec de la mise à jour du statut"); }
  };

  const generateDevis = (i, participants) => {
    const base = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, "") || "";
    const token = localStorage.getItem("map_token") || "";
    const qs = new URLSearchParams({ tarif_index: i, participants: participants || 12, ecole: devisEcole || "" }).toString();
    fetch(`${base}/api/amikobi/program/devis-pdf?${qs}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => { const url = URL.createObjectURL(blob); window.open(url, "_blank"); setTimeout(loadDevisHistory, 800); })
      .catch(() => toast.error("Échec de génération du devis"));
  };

  const redownloadDevis = (h) => {
    const base = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, "") || "";
    const token = localStorage.getItem("map_token") || "";
    const idx = Math.max(0, (doc?.tarifs || []).findIndex((t) => t.nom === h.tarif_nom));
    const qs = new URLSearchParams({
      tarif_index: idx, participants: h.participants || 12, ecole: h.ecole || "",
      numero: h.numero || "", statut: h.statut || "genere",
    }).toString();
    fetch(`${base}/api/amikobi/program/devis-pdf?${qs}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => window.open(URL.createObjectURL(blob), "_blank"))
      .catch(() => toast.error("Échec du téléchargement"));
  };

  const insertOfferTemplate = () => {
    const t = (doc?.tarifs || [])[0] || {};
    setMailSubject("Offre — Kickboxing / Muay-thaï à Amikobi");
    setMailBody(
`Bonjour,

Merci de votre intérêt pour un programme de kickboxing / muay-thaï à Amikobi.

Voici notre offre :
• ${t.nom || "Kickboxing / Muay-thaï"} — ${(t.prix_par_participant ?? 70)} $ par participant
• Session de ${t.duree_semaines ?? 4} semaines · ${t.cours_par_semaine ?? 2} cours/semaine · ${t.minutes_par_cours ?? 50} min/cours
• Minimum de ${t.min_participants ?? 12} participants pour ouvrir le groupe (soit ${((t.prix_par_participant ?? 70) * (t.min_participants ?? 12)).toLocaleString("fr-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $ / session)
• Le tarif comprend l'enseignement, le déplacement et le temps de travail

Je peux vous transmettre un devis détaillé selon le nombre de participants. N'hésitez pas à me revenir avec vos disponibilités.

Au plaisir de collaborer,
Institut Impact Élite Martial — Val-d'Or
819-527-9446`
    );
    toast.success("Modèle d'offre inséré ✓");
  };

  const load = useCallback(async () => {
    try { const { data } = await api.get("/amikobi/program"); setDoc(data); }
    catch { toast.error("Erreur de chargement"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    const next = { ...doc, ...patch };
    setDoc(next);
    try {
      await api.put("/amikobi/program", {
        responsables: next.responsables || [], commanditaires: next.commanditaires || [],
        equipements: next.equipements || [], subventions_pistes: next.subventions_pistes || [],
        tarifs: next.tarifs || [], program_email: next.program_email || "",
        taux_km_officiel: parseFloat(next.taux_km_officiel) || 0,
        objectif_financement: parseFloat(next.objectif_financement) || 0,
      });
    } catch { toast.error("Erreur d'enregistrement"); }
  };
  const addTo = (key, item) => save({ [key]: [...(doc[key] || []), item] });
  const upd = (key, i, patch) => { const l = [...doc[key]]; l[i] = { ...l[i], ...patch }; save({ [key]: l }); };
  const del = (key, i) => save({ [key]: doc[key].filter((_, idx) => idx !== i) });

  const runAi = async (type, url) => {
    setAi({ type, loading: true, data: null });
    try { const { data } = await api.post(url, {}); setAi({ type, loading: false, data }); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur IA"); setAi(null); }
  };

  const sendAmikobiEmail = async () => {
    if (!mailTo.trim()) return toast.error("Destinataire requis");
    if (!mailBody.trim()) return toast.error("Message vide");
    if (!window.confirm(`Envoyer ce courriel à ${mailTo.trim()} ?\n(Réponses vers : ${doc.program_email || "—"})`)) return;
    setMailBusy(true);
    try {
      await api.post("/amikobi/program/send-email", {
        to: mailTo.trim(), subject: mailSubject, body: mailBody, ecole: devisEcole,
        attach_devis: attachDevis, devis_tarif_index: attachIdx, devis_participants: attachP,
      });
      toast.success(attachDevis ? "Courriel + devis envoyés ✓" : "Courriel envoyé ✓");
      setMailBody("");
      if (attachDevis) loadDevisHistory();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur d'envoi"); }
    finally { setMailBusy(false); }
  };

  if (!doc) return <div className="text-zinc-500 py-10 text-center">Chargement…</div>;
  const totalEquip = (doc.equipements || []).reduce((a, e) => a + (parseFloat(e.prix) || 0), 0);
  const inp = "bg-zinc-900 border border-zinc-800 rounded px-2 py-1.5 text-sm text-zinc-100";
  const commConfirme = (doc.commanditaires || []).filter((c) => c.suivi === "accepte").reduce((a, c) => a + (parseFloat(c.montant) || 0), 0);
  const subvAccepte = (doc.subventions_pistes || []).filter((s) => s.suivi === "accepte").reduce((a, s) => a + (parseFloat(s.montant_estime) || 0), 0);
  const totalFin = commConfirme + subvAccepte;
  const objectif = parseFloat(doc.objectif_financement) || 0;
  const ecart = objectif - totalFin;
  const fmt = (n) => `${(n || 0).toFixed(2)} $`;

  const runRelances = async () => {
    try {
      const { data } = await api.post("/amikobi/relances/run?dry_run=true");
      const items = Object.values(data.by_user || {}).flat();
      if (data.count === 0) { toast.success("Aucune relance en attente 👍"); return; }
      toast.info(`${data.count} commanditaire(s) à relancer : ` + items.map((i) => `${i.nom} (${i.jours} j)`).join(", "));
    } catch { toast.error("Erreur"); }
  };
  const dlAuth = async (path, filename, asPdf) => {
    try {
      const resp = await api.get(path, { responseType: "blob" });
      const type = asPdf ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const url = URL.createObjectURL(new Blob([resp.data], { type }));
      if (asPdf) { window.open(url, "_blank"); }
      else { const a = document.createElement("a"); a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove(); }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Erreur d'export"); }
  };
  const orderGroup = () => {
    const names = window.prompt("Noms des athlètes séparés par des virgules :", "");
    if (names === null) return;
    dlAuth(`/amikobi/program/bon-commande-groupe?athletes=${encodeURIComponent(names)}`, "bons_commande_groupe.pdf", true);
  };
  const orderOne = () => {
    const a = window.prompt("Nom de l'athlète (optionnel) :", "");
    if (a === null) return;
    dlAuth(`/amikobi/program/bon-commande${a ? `?athlete=${encodeURIComponent(a)}` : ""}`, "bon_commande_athlete.pdf", true);
  };

  return (
    <div className="space-y-8" data-testid="financement-tab">
      {/* Export financier */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-[11px] tracking-[0.2em] text-zinc-500 uppercase">Tableau de bord — Financement & partenariats</div>
        <div className="flex gap-2">
          <button data-testid="export-pdf-btn" onClick={() => dlAuth("/amikobi/program/export-pdf", "amikobi_financement.pdf", true)} className="text-sm px-3 py-1.5 rounded border border-zinc-700 text-zinc-200 hover:bg-white/5 font-semibold">📄 Export PDF</button>
          <button data-testid="export-xlsx-btn" onClick={() => dlAuth("/amikobi/program/export-xlsx", "amikobi_financement.xlsx", false)} className="text-sm px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-500 text-white font-semibold">📊 Export Excel</button>
        </div>
      </div>
      {/* Tableau de bord financement */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="fin-dashboard">
        <DashCard label="Commandites confirmées" value={fmt(commConfirme)} accent="emerald" />
        <DashCard label="Subventions acceptées" value={fmt(subvAccepte)} accent="sky" />
        <DashCard label="Total financé" value={fmt(totalFin)} accent="amber" />
        <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-3">
          <div className="text-[10px] tracking-[0.2em] text-zinc-500 uppercase">Objectif</div>
          <input type="number" data-testid="objectif-input" value={doc.objectif_financement || 0} onChange={(e) => save({ objectif_financement: parseFloat(e.target.value || 0) })} className={`w-full mt-1 ${inp}`} />
          <div className={`text-xs mt-1 ${ecart > 0 ? "text-amber-400" : "text-emerald-400"}`} data-testid="fin-ecart">{ecart > 0 ? `Écart : ${fmt(ecart)} à trouver` : `Objectif atteint (+${fmt(-ecart)})`}</div>
        </div>
      </div>

      {/* Responsables */}
      <SectionCard title="Responsables du programme">
        <div className="space-y-2">
          {(doc.responsables || []).map((r, i) => (
            <div key={i} className="grid grid-cols-12 gap-2" data-testid={`resp-${i}`}>
              <input className={`col-span-3 ${inp}`} placeholder="Nom" value={r.nom} onChange={(e) => upd("responsables", i, { nom: e.target.value })} />
              <input className={`col-span-3 ${inp}`} placeholder="Rôle" value={r.role} onChange={(e) => upd("responsables", i, { role: e.target.value })} />
              <input className={`col-span-3 ${inp}`} placeholder="Courriel" value={r.courriel} onChange={(e) => upd("responsables", i, { courriel: e.target.value })} />
              <input className={`col-span-2 ${inp}`} placeholder="Tél." value={r.tel} onChange={(e) => upd("responsables", i, { tel: e.target.value })} />
              <button className="col-span-1 text-red-400" onClick={() => del("responsables", i)}>✕</button>
            </div>
          ))}
        </div>
        <button data-testid="add-resp" onClick={() => addTo("responsables", { nom: "", role: "", courriel: "", tel: "" })} className="mt-2 text-sm text-amber-400 hover:underline">+ Ajouter un responsable</button>
      </SectionCard>

      {/* Tarifs du programme */}
      <SectionCard title="Tarifs du programme">
        <div className="space-y-3">
          {(doc.tarifs || []).map((t, i) => {
            const minRevenu = (parseFloat(t.prix_par_participant) || 0) * (parseInt(t.min_participants) || 0);
            return (
              <div key={i} className="rounded-lg border border-zinc-800 bg-zinc-950/50 p-3 space-y-2" data-testid={`tarif-${i}`}>
                <div className="flex gap-2 items-center">
                  <input className={`flex-1 ${inp}`} placeholder="Nom du tarif" value={t.nom} onChange={(e) => upd("tarifs", i, { nom: e.target.value })} data-testid={`tarif-nom-${i}`} />
                  <button className="text-red-400 px-2" onClick={() => del("tarifs", i)} data-testid={`tarif-del-${i}`}>✕</button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  <label className="text-[11px] text-zinc-400">Prix / participant ($)
                    <input type="number" className={`w-full mt-0.5 ${inp}`} value={t.prix_par_participant} onChange={(e) => upd("tarifs", i, { prix_par_participant: parseFloat(e.target.value || 0) })} data-testid={`tarif-prix-${i}`} /></label>
                  <label className="text-[11px] text-zinc-400">Durée (semaines)
                    <input type="number" className={`w-full mt-0.5 ${inp}`} value={t.duree_semaines} onChange={(e) => upd("tarifs", i, { duree_semaines: parseInt(e.target.value || 0) })} /></label>
                  <label className="text-[11px] text-zinc-400">Cours / semaine
                    <input type="number" className={`w-full mt-0.5 ${inp}`} value={t.cours_par_semaine} onChange={(e) => upd("tarifs", i, { cours_par_semaine: parseInt(e.target.value || 0) })} /></label>
                  <label className="text-[11px] text-zinc-400">Minutes / cours
                    <input type="number" className={`w-full mt-0.5 ${inp}`} value={t.minutes_par_cours} onChange={(e) => upd("tarifs", i, { minutes_par_cours: parseInt(e.target.value || 0) })} /></label>
                  <label className="text-[11px] text-zinc-400">Min. participants
                    <input type="number" className={`w-full mt-0.5 ${inp}`} value={t.min_participants} onChange={(e) => upd("tarifs", i, { min_participants: parseInt(e.target.value || 0) })} /></label>
                </div>
                <textarea className={`w-full ${inp}`} rows={2} placeholder="Note (ce qui est compris, déplacement…)" value={t.note} onChange={(e) => upd("tarifs", i, { note: e.target.value })} data-testid={`tarif-note-${i}`} />
                <div className="flex flex-wrap items-center gap-3 border-t border-zinc-800 pt-2">
                  <label className="flex items-center gap-1.5 text-[11px] text-zinc-300">
                    <input type="checkbox" checked={!!t.inclure_deplacement} onChange={(e) => upd("tarifs", i, { inclure_deplacement: e.target.checked })} data-testid={`tarif-depl-check-${i}`} />
                    Ajouter frais de déplacement
                  </label>
                  <label className="text-[11px] text-zinc-400">Km (A-R)
                    <input type="number" className={`w-20 ml-1 ${inp}`} value={t.deplacement_km || 0} onChange={(e) => upd("tarifs", i, { deplacement_km: parseFloat(e.target.value || 0) })} data-testid={`tarif-km-${i}`} /></label>
                  <label className="text-[11px] text-zinc-400">$/km
                    <input type="number" step="0.01" className={`w-20 ml-1 ${inp}`} value={t.taux_km || 0} onChange={(e) => upd("tarifs", i, { taux_km: parseFloat(e.target.value || 0) })} data-testid={`tarif-taux-${i}`} /></label>
                  {t.inclure_deplacement && (
                    <span className="text-[11px] text-sky-300" data-testid={`tarif-depl-montant-${i}`}>+ {((t.deplacement_km || 0) * (t.taux_km || 0)).toLocaleString("fr-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $ déplacement</span>
                  )}
                </div>
                <div className="text-xs text-emerald-400 font-semibold" data-testid={`tarif-min-revenu-${i}`}>Revenu minimum garanti : {minRevenu.toLocaleString("fr-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $ ({t.min_participants || 0} × {(parseFloat(t.prix_par_participant) || 0).toLocaleString("fr-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $) pour {t.duree_semaines || 0} semaines</div>
                <div className="flex flex-wrap items-center gap-2 border-t border-zinc-800 pt-2">
                  <span className="text-[11px] text-zinc-400">Devis pour</span>
                  <input type="number" className={`w-20 ${inp}`} placeholder="12" value={devisP[i] ?? t.min_participants ?? 12} onChange={(e) => setDevisP({ ...devisP, [i]: parseInt(e.target.value || 0) })} data-testid={`devis-participants-${i}`} />
                  <span className="text-[11px] text-zinc-400">participant(s)</span>
                  <button onClick={() => generateDevis(i, devisP[i] ?? t.min_participants ?? 12)} className="px-3 py-1.5 rounded bg-amber-500 text-zinc-950 font-semibold text-xs hover:bg-amber-400" data-testid={`devis-pdf-${i}`}>📄 Devis PDF</button>
                  {(devisP[i] ?? t.min_participants ?? 12) < (t.min_participants || 12) && (
                    <span className="text-[11px] text-red-400 font-semibold" data-testid={`seuil-alert-${i}`}>⚠ Sous le seuil de {t.min_participants || 12} — déplacement possiblement non rentable</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <button data-testid="add-tarif" onClick={() => addTo("tarifs", { nom: "", prix_par_participant: 70, duree_semaines: 4, cours_par_semaine: 2, minutes_par_cours: 50, min_participants: 12, note: "" })} className="mt-2 text-sm text-amber-400 hover:underline">+ Ajouter un tarif</button>
      </SectionCard>

      {/* Courriel de réponse + compositeur */}
      <SectionCard title="Courriel du programme (contacter / répondre)">
        <label className="block text-xs text-zinc-400 mb-3">Courriel de réponse du programme (les réponses reviennent ici)
          <input type="email" className={`w-full max-w-md mt-1 ${inp}`} value={doc.program_email || ""} onChange={(e) => save({ program_email: e.target.value })} placeholder="ex. impactkb.bjj@gmail.com" data-testid="program-email-input" /></label>
        <label className="block text-xs text-zinc-400 mb-3">Taux de déplacement officiel ($/km) — appliqué à tous les devis
          <input type="number" step="0.01" className={`w-32 mt-1 ${inp}`} value={doc.taux_km_officiel ?? 0.70} onChange={(e) => save({ taux_km_officiel: parseFloat(e.target.value || 0) })} data-testid="taux-officiel-input" /></label>
        <div className="rounded-lg border border-zinc-800 bg-zinc-950/50 p-3 space-y-2" data-testid="amikobi-email-composer">
          <div className="flex items-center justify-between gap-2">
            <input type="email" className={`sm:flex-1 ${inp}`} placeholder="École destinataire (pour le devis)" value={devisEcole} onChange={(e) => setDevisEcole(e.target.value)} data-testid="devis-ecole" />
            <button onClick={insertOfferTemplate} className="whitespace-nowrap text-xs font-semibold text-amber-400 hover:underline" data-testid="insert-offer-template">✨ Insérer un modèle d'offre</button>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <input type="email" className={`sm:flex-1 ${inp}`} placeholder="Destinataire" value={mailTo} onChange={(e) => setMailTo(e.target.value)} data-testid="amikobi-mail-to" />
            <input className={`sm:flex-1 ${inp}`} placeholder="Objet" value={mailSubject} onChange={(e) => setMailSubject(e.target.value)} data-testid="amikobi-mail-subject" />
          </div>
          <textarea className={`w-full ${inp}`} rows={5} placeholder="Votre message… (les réponses arriveront à votre courriel de réponse)" value={mailBody} onChange={(e) => setMailBody(e.target.value)} data-testid="amikobi-mail-body" />
          <div className="flex items-center gap-2">
            <button onClick={sendAmikobiEmail} disabled={mailBusy} className="px-4 py-2 rounded bg-emerald-600 hover:bg-emerald-500 text-white font-semibold text-sm disabled:opacity-50" data-testid="amikobi-mail-send">{mailBusy ? "Envoi…" : "✉️ Envoyer"}</button>
            <span className="text-[11px] text-zinc-500">Répondre à : {doc.program_email || "—"}</span>
          </div>
          <div className="flex flex-wrap items-center gap-2 border-t border-zinc-800 pt-2">
            <label className="flex items-center gap-1.5 text-[11px] text-zinc-300">
              <input type="checkbox" checked={attachDevis} onChange={(e) => setAttachDevis(e.target.checked)} data-testid="attach-devis-check" />
              Joindre le devis PDF
            </label>
            {attachDevis && (
              <>
                <select className={`${inp} text-xs`} value={attachIdx} onChange={(e) => setAttachIdx(parseInt(e.target.value))} data-testid="attach-devis-tarif">
                  {(doc.tarifs || []).map((t, i) => <option key={i} value={i}>{t.nom}</option>)}
                </select>
                <input type="number" className={`w-20 ${inp} text-xs`} value={attachP} onChange={(e) => setAttachP(parseInt(e.target.value || 0))} data-testid="attach-devis-participants" />
                <span className="text-[11px] text-zinc-400">participant(s)</span>
              </>
            )}
          </div>
        </div>
      </SectionCard>

      {/* Journal des devis */}
      <SectionCard title="Journal des devis">
        {devisHistory.length === 0 ? <p className="text-xs text-zinc-500">Aucun devis généré pour le moment.</p> : (
          <div className="overflow-x-auto" data-testid="devis-history">
            <table className="w-full text-xs">
              <thead><tr className="text-zinc-400 text-left"><th className="py-1 pr-2">N°</th><th className="py-1 pr-2">Date</th><th className="py-1 pr-2">École</th><th className="py-1 pr-2">Tarif</th><th className="py-1 pr-2 text-center">Particip.</th><th className="py-1 pr-2 text-right">Total</th><th className="py-1">Statut</th></tr></thead>
              <tbody>
                {devisHistory.map((h) => (
                  <tr key={h.id} className="border-t border-zinc-900" data-testid={`devis-row-${h.id}`}>
                    <td className="py-1 pr-2 text-amber-300 font-mono">{h.numero || "—"}</td>
                    <td className="py-1 pr-2 text-zinc-400">{(h.date || h.created_at || "").slice(0, 10)}</td>
                    <td className="py-1 pr-2 text-zinc-200">{h.ecole || (h.envoye_a ? `→ ${h.envoye_a}` : "—")}</td>
                    <td className="py-1 pr-2 text-zinc-300">{h.tarif_nom}</td>
                    <td className="py-1 pr-2 text-center text-zinc-300">{h.participants}</td>
                    <td className="py-1 pr-2 text-right text-emerald-400 font-semibold">{(h.total || 0).toLocaleString("fr-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $</td>
                    <td className="py-1">
                      <div className="flex items-center gap-1.5">
                        <select
                          data-testid={`devis-statut-${h.id}`}
                          value={h.statut || "genere"}
                          onChange={(e) => setDevisStatut(h.id, e.target.value)}
                          className={`text-[11px] rounded px-1.5 py-1 border bg-zinc-950 ${STATUT_CLS[h.statut] || "border-zinc-700 text-zinc-300"}`}
                        >
                          <option value="genere">Généré</option>
                          <option value="envoye">Envoyé</option>
                          <option value="accepte">Accepté</option>
                          <option value="refuse">Refusé</option>
                        </select>
                        <button onClick={() => redownloadDevis(h)} data-testid={`devis-pdf-${h.id}`}
                          className="text-[11px] text-sky-300 hover:text-sky-200 border border-sky-500/30 rounded px-1.5 py-1" title="Télécharger le PDF avec le statut actuel">PDF</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {/* Équipements & uniformes */}
      <SectionCard title={`Équipements & uniformes requis — total ${totalEquip.toFixed(2)} $`}
        headerActions={<div className="flex gap-2">
          <button data-testid="bon-commande-btn" onClick={orderOne} className="text-sm px-3 py-1.5 rounded bg-amber-500 hover:bg-amber-400 text-black font-semibold">🖨️ Bon (1 athlète)</button>
          <button data-testid="bon-commande-groupe-btn" onClick={orderGroup} className="text-sm px-3 py-1.5 rounded bg-amber-600 hover:bg-amber-500 text-white font-semibold">🖨️ Groupe</button>
        </div>}>
        <div className="space-y-2">
          {(doc.equipements || []).map((e, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`equip-${i}`}>
              <input className={`col-span-6 ${inp}`} placeholder="Article" value={e.nom} onChange={(x) => upd("equipements", i, { nom: x.target.value })} />
              <input className={`col-span-2 ${inp}`} type="number" placeholder="Prix" value={e.prix} onChange={(x) => upd("equipements", i, { prix: parseFloat(x.target.value || 0) })} />
              <label className="col-span-3 text-xs flex items-center gap-1 text-zinc-400"><input type="checkbox" checked={e.obligatoire} onChange={(x) => upd("equipements", i, { obligatoire: x.target.checked })} /> Obligatoire</label>
              <button className="col-span-1 text-red-400" onClick={() => del("equipements", i)}>✕</button>
            </div>
          ))}
        </div>
        <button data-testid="add-equip" onClick={() => addTo("equipements", { nom: "", prix: 0, obligatoire: true, note: "" })} className="mt-2 text-sm text-amber-400 hover:underline">+ Ajouter un article</button>
      </SectionCard>

      {/* Commanditaires */}
      <SectionCard title="Commanditaires (actifs & potentiels)"
        headerActions={<div className="flex gap-2">
          <button data-testid="relances-btn" onClick={runRelances} className="text-sm px-3 py-1.5 rounded border border-amber-700 text-amber-300 hover:bg-amber-950/40">🔔 Relances à faire</button>
          <button data-testid="ai-sponsors-btn" onClick={() => runAi("sponsors", "/amikobi/program/ia/commanditaires")} className="text-sm px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-500 text-white">🤖 Trouver des pistes (IA)</button>
        </div>}>
        <div className="space-y-2">
          {(doc.commanditaires || []).map((c, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`sponsor-${i}`}>
              <input className={`col-span-3 ${inp}`} placeholder="Nom" value={c.nom} onChange={(e) => upd("commanditaires", i, { nom: e.target.value })} />
              <select className={`col-span-2 ${inp}`} value={c.statut} onChange={(e) => upd("commanditaires", i, { statut: e.target.value })}>
                <option value="potentiel">Potentiel</option><option value="actif">Actif</option>
              </select>
              <select className={`col-span-2 ${inp}`} data-testid={`sponsor-suivi-${i}`} value={c.suivi || "attente"} onChange={(e) => upd("commanditaires", i, { suivi: e.target.value, suivi_date: e.target.value === "contacte" ? new Date().toISOString().slice(0, 10) : c.suivi_date })}>
                <option value="contacte">Contacté</option><option value="attente">En attente</option><option value="accepte">Accepté</option><option value="refuse">Refusé</option>
              </select>
              <input className={`col-span-2 ${inp}`} type="number" placeholder="Montant" value={c.montant} onChange={(e) => upd("commanditaires", i, { montant: parseFloat(e.target.value || 0) })} />
              <input className={`col-span-2 ${inp}`} placeholder="Courriel" value={c.courriel} onChange={(e) => upd("commanditaires", i, { courriel: e.target.value })} />
              <button className="col-span-1 text-red-400" onClick={() => del("commanditaires", i)}>✕</button>
            </div>
          ))}
        </div>
        <button data-testid="add-sponsor" onClick={() => addTo("commanditaires", { nom: "", statut: "potentiel", montant: 0, courriel: "", note: "" })} className="mt-2 text-sm text-amber-400 hover:underline">+ Ajouter un commanditaire</button>
        {ai?.type === "sponsors" && <AiLeads ai={ai} onAdd={(p) => addTo("commanditaires", { nom: p.nom, statut: "potentiel", montant: p.montant_suggere || 0, note: `${p.pourquoi || ""} ${p.approche || ""}`.trim() })} field="montant_suggere" />}
      </SectionCard>

      {/* Subventions potentielles */}
      <SectionCard title="Pistes de subventions"
        headerActions={<button data-testid="ai-grants-btn" onClick={() => runAi("grants", "/amikobi/program/ia/subventions")} className="text-sm px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-500 text-white">🤖 Trouver des subventions (IA)</button>}>
        <div className="space-y-2">
          {(doc.subventions_pistes || []).map((s, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`grant-${i}`}>
              <input className={`col-span-3 ${inp}`} placeholder="Programme" value={s.nom} onChange={(e) => upd("subventions_pistes", i, { nom: e.target.value })} />
              <input className={`col-span-3 ${inp}`} placeholder="Organisme" value={s.organisme} onChange={(e) => upd("subventions_pistes", i, { organisme: e.target.value })} />
              <select className={`col-span-2 ${inp}`} data-testid={`grant-suivi-${i}`} value={s.suivi || "attente"} onChange={(e) => upd("subventions_pistes", i, { suivi: e.target.value })}>
                <option value="contacte">Contacté</option><option value="attente">En attente</option><option value="accepte">Accepté</option><option value="refuse">Refusé</option>
              </select>
              <input className={`col-span-3 ${inp}`} type="number" placeholder="Montant estimé" value={s.montant_estime} onChange={(e) => upd("subventions_pistes", i, { montant_estime: parseFloat(e.target.value || 0) })} />
              <button className="col-span-1 text-red-400" onClick={() => del("subventions_pistes", i)}>✕</button>
            </div>
          ))}
        </div>
        <button data-testid="add-grant" onClick={() => addTo("subventions_pistes", { nom: "", organisme: "", montant_estime: 0, echeance: "", lien: "", note: "" })} className="mt-2 text-sm text-amber-400 hover:underline">+ Ajouter une piste</button>
        {ai?.type === "grants" && <AiLeads ai={ai} onAdd={(p) => addTo("subventions_pistes", { nom: p.nom, organisme: p.organisme || "", montant_estime: p.montant_estime || 0, note: p.pourquoi || "" })} field="montant_estime" />}
      </SectionCard>
    </div>
  );
}

function AiLeads({ ai, onAdd, field }) {
  if (ai.loading) return <div className="mt-3 text-sm text-zinc-500">🤖 L'IA cherche des pistes…</div>;
  const pistes = ai.data?.pistes || [];
  return (
    <div className="mt-4 border-t border-zinc-800 pt-3 space-y-2" data-testid="ai-leads">
      <div className="text-xs uppercase tracking-widest text-emerald-400">Pistes suggérées par l'IA</div>
      {pistes.map((p, i) => (
        <div key={i} className="flex items-start justify-between gap-3 p-2 rounded border border-zinc-800" data-testid={`ai-lead-${i}`}>
          <div className="text-sm">
            <div className="font-semibold text-zinc-100">{p.nom} {p[field] ? <span className="text-amber-400">· {p[field]} $</span> : null}</div>
            <div className="text-xs text-zinc-500">{p.organisme ? `${p.organisme} — ` : ""}{p.pourquoi || p.approche}</div>
          </div>
          <button onClick={() => onAdd(p)} data-testid={`ai-lead-add-${i}`} className="text-xs px-2 py-1 rounded bg-amber-500 hover:bg-amber-400 text-black font-semibold whitespace-nowrap">+ Ajouter</button>
        </div>
      ))}
      {pistes.length === 0 && <div className="text-sm text-zinc-500">Aucune piste.</div>}
    </div>
  );
}

function DashCard({ label, value, accent }) {
  const c = { emerald: "text-emerald-300", sky: "text-sky-300", amber: "text-amber-300" };
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-3">
      <div className="text-[10px] tracking-[0.2em] text-zinc-500 uppercase">{label}</div>
      <div className={`text-xl font-bold mt-1 ${c[accent] || "text-zinc-100"}`}>{value}</div>
    </div>
  );
}

const PILOT_STATUS = [
  { key: "proposition", label: "Proposition envoyée", cls: "bg-zinc-600 text-white" },
  { key: "rencontre", label: "Rencontre à planifier", cls: "bg-amber-500 text-zinc-950" },
  { key: "pilote_actif", label: "Pilote actif", cls: "bg-emerald-500 text-zinc-950" },
  { key: "en_pause", label: "En pause", cls: "bg-sky-600 text-white" },
  { key: "termine", label: "Terminé", cls: "bg-red-500 text-white" },
];

function PilotTab({ reload, onGoTab }) {
  const [doc, setDoc] = useState(null);
  const [saving, setSaving] = useState(false);
  const [recruitNames, setRecruitNames] = useState("");
  const [recruiting, setRecruiting] = useState(false);

  const downloadDossier = async () => {
    try {
      const r = await api.get("/amikobi/pilot/dossier-pdf", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = Object.assign(document.createElement("a"), { href: url, download: "Dossier_Projet_Pilote_Amikobi.pdf" });
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success("Dossier officiel 4 pages téléchargé — joins-le au courriel court");
    } catch { toast.error("Impossible de générer le dossier"); }
  };
  const [dossierTo, setDossierTo] = useState("");
  const [dossierCc, setDossierCc] = useState("");
  const [dossierBudget, setDossierBudget] = useState(true);
  const [dossierBusy, setDossierBusy] = useState(false);
  const sendDossier = async () => {
    const to = dossierTo.split(/[,;\s]+/).filter((e) => e.includes("@"));
    if (!to.length) { toast.error("Indique au moins un courriel de dirigeant"); return; }
    if (!window.confirm(`Préparer le courriel aux dirigeants (${to.join(", ")}) avec le dossier 4 pages${dossierBudget ? " + l'annexe budget/devis" : ""} ?\n\nSi Gmail est connecté : un BROUILLON est créé (tu relis et envoies). Sinon : envoi direct.`)) return;
    setDossierBusy(true);
    try {
      const { data } = await api.post("/amikobi/pilot/send-dossier", { to, cc: dossierCc.split(/[,;\s]+/).filter((e) => e.includes("@")), include_budget: dossierBudget, participants: doc?.target_students || 20 });
      toast.success(data.message, { duration: 10000 });
    } catch (e) { toast.error(e.response?.data?.detail || "Envoi impossible"); } finally { setDossierBusy(false); }
  };
  const copyCoverEmail = async () => {
    try {
      const r = await api.get("/amikobi/pilot/cover-email");
      await navigator.clipboard.writeText(`Objet : ${r.data.subject}\n\n${r.data.body}`);
      toast.success("Courriel d'accompagnement copié (8-12 lignes) — colle-le dans Gmail et joins le PDF");
    } catch { toast.error("Copie impossible"); }
  };
  const downloadLetter = async () => {
    try {
      const r = await api.get("/amikobi/pilot/letter-pdf", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Impossible de générer la lettre"); }
  };

  const recruit = async () => {
    const names = recruitNames.split("\n").map((n) => n.trim()).filter(Boolean);
    if (names.length === 0) return toast.error("Ajoute au moins un nom");
    setRecruiting(true);
    try {
      const { data } = await api.post("/amikobi/pilot/bulk-recruit", { names });
      toast.success(`${data.enrolled} inscrit(s) · ${data.skipped} déjà présent(s).`);
      setRecruitNames("");
      reload && reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setRecruiting(false); }
  };

  const load = useCallback(async () => {
    try { const { data } = await api.get("/amikobi/pilot"); setDoc(data); }
    catch { toast.error("Erreur de chargement"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const persist = async (next) => {
    setDoc(next);
    setSaving(true);
    try {
      await api.put("/amikobi/pilot", {
        school_name: next.school_name || "", resource_person: next.resource_person || "",
        resource_role: next.resource_role || "", porteur: next.porteur || "",
        status: next.status || "proposition", deployment: next.deployment || "",
        target_students: parseInt(next.target_students) || 0,
        sessions_per_week: parseInt(next.sessions_per_week) || 0,
        objectives: next.objectives || [], steps: next.steps || [],
        notes: next.notes || "", program_id: next.program_id || null, cohort_id: next.cohort_id || null,
      });
    } catch { toast.error("Erreur d'enregistrement"); }
    finally { setSaving(false); }
  };

  const seed = async () => {
    try {
      const { data } = await api.post("/amikobi/pilot/seed-from-letter");
      toast.success(data.created ? "Cohorte pilote créée depuis la lettre ! 🥋" : "Projet pilote déjà initialisé — relié au programme/cohorte.");
      await load();
      reload && reload();
    } catch { toast.error("Erreur d'initialisation"); }
  };

  if (!doc) return <div className="text-zinc-500 py-10 text-center">Chargement…</div>;

  const notInit = !doc.program_id && (doc.objectives || []).length === 0;
  const st = PILOT_STATUS.find((s) => s.key === doc.status) || PILOT_STATUS[0];
  const doneSteps = (doc.steps || []).filter((s) => s.done).length;
  const inp = "bg-zinc-900 border border-zinc-800 rounded px-2 py-1.5 text-sm text-zinc-100";

  return (
    <div className="space-y-6" data-testid="pilot-tab">
      {notInit && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-5 flex flex-wrap items-center justify-between gap-4" data-testid="pilot-empty">
          <div>
            <div className="text-amber-200 font-semibold">Projet pilote non initialisé</div>
            <div className="text-sm text-zinc-400 mt-1">Crée la cohorte pilote, le programme et la fiche partenariat à partir de la lettre de présentation Amikobi.</div>
          </div>
          <button data-testid="seed-pilot-btn" onClick={seed} className={btnPrimary}><Plus size={16} /> Initialiser depuis la lettre</button>
        </div>
      )}

      {/* En-tête partenariat */}
      <SectionCard title="Partenariat — École Amikobi"
        headerActions={
          <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${st.cls}`} data-testid="pilot-status-badge">{st.label}</span>
        }>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-zinc-500">École partenaire</label>
            <input data-testid="pilot-school" className={`w-full mt-1 ${inp}`} value={doc.school_name || ""} onChange={(e) => setDoc({ ...doc, school_name: e.target.value })} onBlur={() => persist(doc)} />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-zinc-500">Statut du projet</label>
            <select data-testid="pilot-status" className={`w-full mt-1 ${inp}`} value={doc.status} onChange={(e) => persist({ ...doc, status: e.target.value })}>
              {PILOT_STATUS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-zinc-500">Personne-ressource</label>
            <input data-testid="pilot-resource" className={`w-full mt-1 ${inp}`} value={doc.resource_person || ""} onChange={(e) => setDoc({ ...doc, resource_person: e.target.value })} onBlur={() => persist(doc)} />
            <input className={`w-full mt-1 ${inp}`} placeholder="Rôle" value={doc.resource_role || ""} onChange={(e) => setDoc({ ...doc, resource_role: e.target.value })} onBlur={() => persist(doc)} />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-zinc-500">Porteur du projet</label>
            <input data-testid="pilot-porteur" className={`w-full mt-1 ${inp}`} value={doc.porteur || ""} onChange={(e) => setDoc({ ...doc, porteur: e.target.value })} onBlur={() => persist(doc)} />
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="text-[11px] uppercase tracking-wider text-zinc-500">Élèves visés</label>
              <input type="number" data-testid="pilot-target" className={`w-full mt-1 ${inp}`} value={doc.target_students || 0} onChange={(e) => setDoc({ ...doc, target_students: e.target.value })} onBlur={() => persist(doc)} />
            </div>
            <div className="flex-1">
              <label className="text-[11px] uppercase tracking-wider text-zinc-500">Séances / sem.</label>
              <input type="number" className={`w-full mt-1 ${inp}`} value={doc.sessions_per_week || 0} onChange={(e) => setDoc({ ...doc, sessions_per_week: e.target.value })} onBlur={() => persist(doc)} />
            </div>
          </div>
          <div className="md:col-span-2">
            <label className="text-[11px] uppercase tracking-wider text-zinc-500">Plan de déploiement</label>
            <textarea data-testid="pilot-deployment" rows={3} className={`w-full mt-1 ${inp} resize-none`} value={doc.deployment || ""} onChange={(e) => setDoc({ ...doc, deployment: e.target.value })} onBlur={() => persist(doc)} />
          </div>
        </div>
        {saving && <div className="text-[11px] text-zinc-500 mt-2">Enregistrement…</div>}
      </SectionCard>

      {/* Objectifs éducatifs */}
      <SectionCard title="Objectifs éducatifs">
        <div className="space-y-2">
          {(doc.objectives || []).map((o, i) => (
            <div key={i} className="flex gap-2 items-center" data-testid={`pilot-obj-${i}`}>
              <input className={`flex-1 ${inp}`} value={o} onChange={(e) => { const l = [...doc.objectives]; l[i] = e.target.value; setDoc({ ...doc, objectives: l }); }} onBlur={() => persist(doc)} />
              <button className="text-red-400" onClick={() => persist({ ...doc, objectives: doc.objectives.filter((_, idx) => idx !== i) })}><Trash size={15} /></button>
            </div>
          ))}
        </div>
        <button data-testid="add-objective" onClick={() => persist({ ...doc, objectives: [...(doc.objectives || []), ""] })} className="mt-2 text-sm text-amber-400 hover:underline">+ Ajouter un objectif</button>
      </SectionCard>

      {/* Prochaines étapes */}
      <SectionCard title={`Prochaines étapes (${doneSteps}/${(doc.steps || []).length})`}>
        <div className="space-y-2">
          {(doc.steps || []).map((s, i) => (
            <div key={s.id || i} className="flex gap-3 items-center" data-testid={`pilot-step-${i}`}>
              <input type="checkbox" data-testid={`pilot-step-check-${i}`} checked={!!s.done} onChange={(e) => { const l = [...doc.steps]; l[i] = { ...l[i], done: e.target.checked, date: e.target.checked ? new Date().toISOString().slice(0, 10) : null }; persist({ ...doc, steps: l }); }} className="w-4 h-4 accent-amber-500" />
              <input className={`flex-1 ${inp} ${s.done ? "line-through text-zinc-500" : ""}`} value={s.label} onChange={(e) => { const l = [...doc.steps]; l[i] = { ...l[i], label: e.target.value }; setDoc({ ...doc, steps: l }); }} onBlur={() => persist(doc)} />
              {s.date && <span className="text-[11px] text-emerald-400 font-mono">{s.date}</span>}
              <button className="text-red-400" onClick={() => persist({ ...doc, steps: doc.steps.filter((_, idx) => idx !== i) })}><Trash size={15} /></button>
            </div>
          ))}
        </div>
        <button data-testid="add-step" onClick={() => persist({ ...doc, steps: [...(doc.steps || []), { id: crypto.randomUUID(), label: "", done: false, date: null }] })} className="mt-2 text-sm text-amber-400 hover:underline">+ Ajouter une étape</button>
      </SectionCard>

      {/* Raccourcis */}
      <div className="flex flex-wrap gap-3">
        <button onClick={() => onGoTab && onGoTab("cohorts")} className={btnGhost} data-testid="pilot-go-cohorts"><Users size={15} /> Voir la cohorte pilote</button>
        <button onClick={() => onGoTab && onGoTab("financement")} className={btnGhost} data-testid="pilot-go-financement"><ChartLineUp size={15} /> Matériel & financement</button>
        <button onClick={downloadLetter} className={btnGhost} data-testid="pilot-letter-pdf"><FileText size={15} /> Télécharger la lettre PDF</button>
        <button onClick={downloadDossier} className={btnGhost} data-testid="pilot-dossier-pdf"><FileText size={15} /> Dossier officiel 4 pages (PDF dirigeants)</button>
        <button onClick={copyCoverEmail} className={btnGhost} data-testid="pilot-cover-email">✉️ Copier le courriel court d'accompagnement</button>
      </div>
      <div className="mt-3 rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-3 text-xs space-y-2" data-testid="pilot-send-dossier">
        <p className="text-emerald-200 font-semibold">📨 Envoyer le dossier aux dirigeants en un clic (courriel court + dossier PDF 4 pages + annexe budget/devis)</p>
        <div className="grid sm:grid-cols-2 gap-2">
          <input value={dossierTo} onChange={(e) => setDossierTo(e.target.value)} placeholder="Destinataires (direction, conseil…) — séparés par virgule" className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-zinc-100" data-testid="pilot-dossier-to" />
          <input value={dossierCc} onChange={(e) => setDossierCc(e.target.value)} placeholder="Cc (ex. personne-ressource, prof d'éduc)" className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-zinc-100" data-testid="pilot-dossier-cc" />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-1.5 text-zinc-300"><input type="checkbox" checked={dossierBudget} onChange={(e) => setDossierBudget(e.target.checked)} data-testid="pilot-dossier-budget" /> Joindre l'annexe budget / devis (PDF séparé)</label>
          <button onClick={sendDossier} disabled={dossierBusy} className="px-4 py-2 rounded-lg bg-emerald-700 hover:bg-emerald-600 text-white font-semibold disabled:opacity-40" data-testid="pilot-dossier-send">{dossierBusy ? "Préparation…" : "📨 Préparer dans Gmail (brouillon) / envoyer"}</button>
          <span className="text-zinc-500">Gmail connecté → brouillon à relire ; sinon envoi direct via la plateforme.</span>
        </div>
      </div>

      <AmikobiLeaderFollowups refreshKey={dossierBusy} />

      {/* Recrutement de la cohorte pilote */}
      <SectionCard title="Recrutement de la cohorte pilote">
        <p className="text-sm text-zinc-400 mb-2">Colle la liste des jeunes (un nom par ligne). Ils seront créés comme élèves de l'École Amikobi et inscrits dans la cohorte pilote.</p>
        <textarea
          data-testid="recruit-names"
          rows={6}
          placeholder={"Alex Tremblay\nMaya Wabanonik\nNoah Gagné\n…"}
          value={recruitNames}
          onChange={(e) => setRecruitNames(e.target.value)}
          className={`w-full ${inp} resize-none`}
        />
        <div className="flex items-center gap-3 mt-2">
          <button data-testid="recruit-btn" onClick={recruit} disabled={recruiting} className={btnPrimary}>
            <Plus size={16} /> {recruiting ? "Ajout en cours…" : "Ajouter & inscrire"}
          </button>
          <span className="text-xs text-zinc-500">{(recruitNames.split("\n").filter((n) => n.trim()).length)} nom(s)</span>
        </div>
      </SectionCard>

      {/* Notes */}
      <SectionCard title="Notes">
        <textarea data-testid="pilot-notes" rows={3} className={`w-full ${inp} resize-none`} value={doc.notes || ""} onChange={(e) => setDoc({ ...doc, notes: e.target.value })} onBlur={() => persist(doc)} />
      </SectionCard>
    </div>
  );
}
